import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '../../../shared/Theme/theme';
import Loader from '../../../shared/SmallComponents/Loader';
import { ColumnType, specialColumnType } from '../../../shared/Grid/types/Column';
import Grid from '../../../shared/Grid/Grid';
import Button from '../../../shared/SmallComponents/Button';
import CustomModal, { ModalTriggerProps } from '../../../shared/Modal/Modal';
import { settings, shipment } from '../../../../models/entities/shipment/shipment';
import { formMode } from './AccountingForm/AccountingFormConnected';
import { featureFlagsModel } from '../../../../models/entities/businessPartner/settings';
import { billingLine } from '../../../../models/entities/common_subentities/billingLine';
import mockData from '../../../../sideEffects/shipments/settings/mockData';
import { DropDown } from '../../../shared/inputs/base';
import renderStateOption from '../../../shared/SmallComponents/StateOption';
import CZTooltip from '../../../shared/CZTooltip/CZTooltip';
import infoIcon from '../../../../static/icons/info.svg';
import { currencyFormatter } from '../../../shared/inputs/form/CurrencyInput/CurrencyInput';
import { localSDK as client } from '../../../../sdk';
import { resolveFxMultiplier } from './frankfurterFx';
import {
    billingLineInvoiceDetail,
    formatInvoiceDetailsCell,
    loadInvoiceDetailsByLineId
} from './invoiceLineTracking';

/** Only treat profit as present when we can parse a real number (excludes null, undefined, blank). Zero is included. */
function hasPresentProfitValue(profit: unknown): boolean {
    if (profit === null || profit === undefined) return false;
    if (typeof profit === 'string' && profit.trim() === '') return false;
    const n = Number(profit);
    return !Number.isNaN(n);
}

function roundMoney(n: number): number {
    return Math.round(n * 100) / 100;
}

function profitAfterCargoOwnerSurcharge(profit: number, surchargePercent: number | null | undefined): number {
    const p = surchargePercent == null || surchargePercent === undefined ? 0 : Number(surchargePercent);
    if (!Number.isFinite(p) || p <= 0) return roundMoney(Number(profit) || 0);
    return roundMoney((Number(profit) || 0) * (1 + p / 100));
}

type ProfitInBaseCellState =
    | { status: 'no_base' }
    | { status: 'loading' }
    | { status: 'skip' }
    | { status: 'fail' }
    | {
          status: 'ok';
          /** Profit in base including cargo-owner surcharge (null if line has no parsable profit). */
          converted: number | null;
          totalCostInBase: number;
          rate: number;
          dateLabel: string;
          lineCurrency: string;
          baseCurrency: string;
          profitRaw: number;
          profitDisplay: number;
      };

type Props<entityType, settingsType> = {
    entity: entityType;
    updateShipmentAccountingState: (shipmentId: string, shipmentAccountingState: string) => Promise<void>;
    billingLines?: Array<billingLine>;
    localization: any;
    settings?: settingsType;
    menuType: specialColumnType;
    AccountingFormConnected: any;
    printMode?: boolean;
    featureFlags?: featureFlagsModel;
    /** When set (e.g. from shipment cargo owner), avoids an extra BP fetch for base currency. */
    cargoOwnerBaseCurrency?: string | null;
    cargoOwnerProfitSurchargePercent?: number | null;
    cargoOwnerVat?: number | null;
    /** Bump after send-invoice so invoice details column refetches. */
    invoiceDetailsRefreshToken?: number;
    /** e.g. Send invoice button on shipment accounting toolbar */
    toolbarActions?: React.ReactNode;
};

type AccountingStateOption = {
    text: string;
    value: string;
    color: string;
};

function BillingLines<entityType extends shipment, settingsType extends settings>({
    entity,
    updateShipmentAccountingState,
    billingLines,
    localization,
    menuType,
    AccountingFormConnected,
    settings,
    printMode,
    featureFlags,
    cargoOwnerBaseCurrency: cargoOwnerBaseCurrencyFromParent,
    cargoOwnerProfitSurchargePercent,
    cargoOwnerVat,
    invoiceDetailsRefreshToken,
    toolbarActions
}: Props<entityType, settingsType>) {
    const surchargePct = cargoOwnerProfitSurchargePercent ?? null;
    const serviceTypeName = (serviceType: string) => {
        const serviceName = mockData.en.billingLineServiceType.find((service) => service.code === serviceType)?.name;
        return serviceName || serviceType;
    };

    const calculateTotalProfitByCurrency = () => {
        const totals: { [key: string]: number } = {};

        billingLines?.forEach((line) => {
            if (!hasPresentProfitValue(line.profit)) return;
            const c = String(line.currency ?? '').trim();
            if (!c) return;
            const p = profitAfterCargoOwnerSurcharge(Number(line.profit), surchargePct);
            if (totals[c]) {
                totals[c] += p;
            } else {
                totals[c] = p;
            }
        });

        return totals;
    };

    type BaseTotalState =
        | { status: 'idle' }
        | { status: 'loading' }
        | { status: 'ready'; currency: string; total: number }
        | { status: 'error'; message: string };

    const fxCacheRef = useRef<Map<string, number>>(new Map());
    const [cargoOwnerBaseCurrency, setCargoOwnerBaseCurrency] = useState<string | null>(null);
    const [baseTotal, setBaseTotal] = useState<BaseTotalState>({ status: 'idle' });
    const [lineProfitInBaseCells, setLineProfitInBaseCells] = useState<Record<number, ProfitInBaseCellState>>({});
    const [invoiceDetailsByLineId, setInvoiceDetailsByLineId] = useState<Record<number, billingLineInvoiceDetail>>({});

    const cargoOwnerId = entity.cargoOwnerId;

    const formatTotalInBaseLabel = useMemo(() => {
        const template = localization?.billing_lines?.total_profit_in_base ?? 'Total ({currency})';
        return (currencyCode: string) => template.replace(/\{currency\}/g, currencyCode);
    }, [localization]);

    const baseTotalTooltip =
        localization?.billing_lines?.total_profit_in_base_tooltip ??
        'Sum of each line’s profit converted into the cargo owner’s base currency, using published FX rates for each line’s billing date. Hover a row’s converted profit to see the rate and calculation for that line.';

    const baseTotalInfoAria = localization?.billing_lines?.total_profit_in_base_info_aria ?? 'About total in base currency';

    const baseConversionErrorMessage =
        localization?.billing_lines?.total_profit_in_base_conversion_error ??
        'Could not load exchange rates for one or more billing lines. Please try again.';

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            const fromParent = (cargoOwnerBaseCurrencyFromParent ?? '').trim();
            if (fromParent) {
                if (!cancelled) setCargoOwnerBaseCurrency(fromParent.toUpperCase());
                return;
            }
            if (!cargoOwnerId) {
                if (!cancelled) setCargoOwnerBaseCurrency(null);
                return;
            }
            try {
                const bp = await client.businessPartners().fetchById(cargoOwnerId as any);
                const base = (bp?.baseCurrency as string | undefined)?.trim().toUpperCase() || null;
                if (!cancelled) setCargoOwnerBaseCurrency(base);
            } catch {
                if (!cancelled) setCargoOwnerBaseCurrency(null);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [cargoOwnerId, cargoOwnerBaseCurrencyFromParent]);

    const billingLineIdsKey = billingLines?.map((l) => l.id).join(',') ?? '';

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (!entity.id || !billingLines?.length) {
                setInvoiceDetailsByLineId({});
                return;
            }
            try {
                const map = await loadInvoiceDetailsByLineId(entity.id, entity.cargoOwnerId);
                if (!cancelled) setInvoiceDetailsByLineId(map);
            } catch {
                if (!cancelled) setInvoiceDetailsByLineId({});
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [entity.id, entity.cargoOwnerId, billingLineIdsKey, invoiceDetailsRefreshToken]);

    const billingLinesForGrid = useMemo(() => {
        if (!billingLines?.length) return [];
        return billingLines.map((line) => {
            const detail = line.id != null ? invoiceDetailsByLineId[Number(line.id)] : undefined;
            return {
                ...line,
                invoiceDetails: formatInvoiceDetailsCell(detail)
            };
        });
    }, [billingLines, invoiceDetailsByLineId]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!billingLines?.length) {
                setBaseTotal({ status: 'idle' });
                setLineProfitInBaseCells({});
                return;
            }

            const base = cargoOwnerBaseCurrency;
            if (!base) {
                setBaseTotal({ status: 'idle' });
                setLineProfitInBaseCells(
                    Object.fromEntries(billingLines.map((l) => [l.id, { status: 'no_base' as const }]))
                );
                return;
            }

            setBaseTotal({ status: 'loading' });
            setLineProfitInBaseCells(
                Object.fromEntries(billingLines.map((l) => [l.id, { status: 'loading' as const }]))
            );

            const lineResults = await Promise.all(
                billingLines.map(async (line) => {
                    const lineCurrency = String(line.currency ?? '').trim().toUpperCase();
                    if (!lineCurrency) {
                        return { lineId: line.id, cell: { status: 'skip' as const } };
                    }

                    const rawDate = line.date ? new Date(line.date as any) : new Date();
                    const lineDate = Number.isNaN(rawDate.getTime()) ? new Date() : rawDate;
                    const profitRaw = Number(line.profit);
                    const hasProfit = hasPresentProfitValue(line.profit);
                    const profitDisplay = profitAfterCargoOwnerSurcharge(profitRaw, surchargePct);

                    const mult = await resolveFxMultiplier(lineCurrency, base, lineDate, fxCacheRef.current);
                    if (mult == null) {
                        return { lineId: line.id, cell: { status: 'fail' as const } };
                    }

                    const profitConverted = hasProfit ? roundMoney(profitDisplay * mult) : null;
                    const totalCostNum = Number(line.totalCost) || 0;
                    const totalCostInBase = roundMoney(totalCostNum * mult);

                    const dateLabel = lineDate.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    });

                    return {
                        lineId: line.id,
                        cell: {
                            status: 'ok' as const,
                            converted: profitConverted,
                            totalCostInBase,
                            rate: mult,
                            dateLabel,
                            lineCurrency,
                            baseCurrency: base,
                            profitRaw,
                            profitDisplay
                        }
                    };
                })
            );

            if (cancelled) return;

            const nextMap: Record<number, ProfitInBaseCellState> = {};
            for (const r of lineResults) {
                nextMap[r.lineId] = r.cell;
            }
            setLineProfitInBaseCells(nextMap);

            if (lineResults.some((r) => r.cell.status === 'fail')) {
                setBaseTotal({ status: 'error', message: baseConversionErrorMessage });
                return;
            }

            const total = lineResults
                .filter(
                    (r): r is { lineId: number; cell: Extract<ProfitInBaseCellState, { status: 'ok' }> } =>
                        r.cell.status === 'ok' && r.cell.converted != null
                )
                .reduce((sum, r) => sum + (r.cell.converted as number), 0);

            setBaseTotal({ status: 'ready', currency: base, total: Math.round(total * 100) / 100 });
        };

        run().catch((err) => {
            if (cancelled) return;
            setBaseTotal({ status: 'error', message: err?.message || baseConversionErrorMessage });
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- baseConversionErrorMessage is stable text; including localization would retrigger on every parent render.
    }, [billingLines, cargoOwnerBaseCurrency, surchargePct]);
    const [selectedState, setSelectedState] = useState<string | undefined>(entity.accountingState);
    const [loading, setLoading] = useState<boolean>(false);

    const handleAccountingStateChange = async (selectedValue: string) => {
        try {
            setLoading(true);
            await updateShipmentAccountingState(entity.id, selectedValue);
            setSelectedState(selectedValue);
        } catch (error) {
            console.error('Failed to update accounting state:', error);
        } finally {
            setLoading(false);
        }
    };

    const accountingGridColumns = useMemo((): Array<ColumnType<billingLine>> => {
        const bl = localization?.billing_lines;

        const formatRateForTooltip = (r: number) => {
            const s = r.toFixed(8);
            return s.replace(/\.?0+$/, '') || '0';
        };

        return [
        {
            field: 'id',
            sortable: false,
            isDisplayed: false
        },
        {
            field: 'serviceType',
            sortable: false,
            isDisplayed: true,
            renderer: (cellValue, bindedData) => {
                return <div>{serviceTypeName(cellValue)}</div>;
            }
        },
        {
            field: 'currency',
            sortable: true,
            isDisplayed: true
        },
        {
            field: 'income',
            sortable: true,
            isDisplayed: true
        },
        {
            field: 'expense',
            sortable: true,
            isDisplayed: true
        },
        {
            field: 'totalCost',
            sortable: true,
            isDisplayed: true
        },
        {
            field: 'totalCostBase',
            bindedFields: ['id'],
            sortable: false,
            isDisplayed: true,
            title: `${bl?.columns?.totalCostBase ?? 'Total cost (base)'}${cargoOwnerBaseCurrency ? ` (${cargoOwnerBaseCurrency})` : ''}`,
            renderer: (_cellValue, bindData) => {
                const id = bindData?.[0] as number | undefined;
                const cell = id != null ? lineProfitInBaseCells[id] : undefined;
                const noBaseText = bl?.profit_in_base_cell_no_base ?? 'Set a base currency on the cargo owner to see converted amounts.';
                const loadingText = bl?.profit_in_base_cell_loading ?? 'Loading…';
                const failText = bl?.profit_in_base_cell_failed ?? '';

                if (!cargoOwnerBaseCurrency) {
                    return (
                        <CZTooltip text={noBaseText}>
                            <span style={{ color: '#888', cursor: 'help' }}>—</span>
                        </CZTooltip>
                    );
                }
                if (!cell || cell.status === 'loading') {
                    return (
                        <CZTooltip text={loadingText}>
                            <span style={{ color: '#888', cursor: 'help' }}>…</span>
                        </CZTooltip>
                    );
                }
                if (cell.status === 'no_base' || cell.status === 'skip') {
                    return (
                        <CZTooltip text={noBaseText}>
                            <span style={{ color: '#888', cursor: 'help' }}>—</span>
                        </CZTooltip>
                    );
                }
                if (cell.status === 'fail') {
                    return (
                        <CZTooltip text={failText}>
                            <span style={{ color: '#c00', cursor: 'help' }}>—</span>
                        </CZTooltip>
                    );
                }
                const base = cell.baseCurrency;
                const v = cell.totalCostInBase;
                return <div>{currencyFormatter(base)(v)}</div>;
            }
        },
        {
            field: 'relativeCost',
            sortable: false,
            isDisplayed: true,
            renderer: (cellValue: any, bindData?: any[] | null | undefined) => {
                return <div>{cellValue}%</div>;
            }
        },
        {
            field: 'date',
            sortable: false,
            isDisplayed: true,
            renderer: (cellValue: any, bindData?: any[] | null | undefined) => {
                const date = new Date(cellValue);
                const formattedDate =
                    date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }) +
                    ' at ' +
                    date.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });

                return <div>{formattedDate}</div>;
            }
        },
        {
            field: 'invoiceDetails',
            sortable: false,
            isDisplayed: true,
            title: bl?.columns?.invoice_details ?? 'Invoice details',
            renderer: (cellValue) => {
                const text = (cellValue as string) || '—';
                const uninvoiced = text === '—';
                return <div style={{ color: uninvoiced ? '#9ca3af' : undefined }}>{text}</div>;
            }
        },
        {
            field: 'remarks',
            sortable: false,
            isDisplayed: true
        },
        {
            field: 'profit',
            sortable: false,
            isDisplayed: true,
            toolTipText:
                surchargePct && surchargePct > 0
                    ? `Profit including ${surchargePct}% cargo owner surcharge.`
                    : undefined,
            renderer: (cellValue: number) => {
                const v = profitAfterCargoOwnerSurcharge(Number(cellValue), surchargePct);
                let color = 'black';
                v < 0 ? (color = 'red') : v > 0 ? (color = 'green') : (color = 'black');
                return <div style={{ color: color }}>{v}</div>;
            }
        },
        {
            field: 'profitInBase',
            bindedFields: ['id'],
            sortable: false,
            isDisplayed: true,
            renderer: (_cellValue, bindData) => {
                const id = bindData?.[0] as number | undefined;
                const cell = id != null ? lineProfitInBaseCells[id] : undefined;

                const noBaseText = bl?.profit_in_base_cell_no_base ?? 'Set a base currency on the cargo owner to see converted amounts.';
                const skipText = bl?.profit_in_base_cell_skip ?? '';
                const failText = bl?.profit_in_base_cell_failed ?? '';
                const loadingText = bl?.profit_in_base_cell_loading ?? 'Loading…';
                const tpl = bl?.profit_in_base_cell_tooltip ?? '';

                if (!cargoOwnerBaseCurrency) {
                    return (
                        <CZTooltip text={noBaseText}>
                            <span style={{ color: '#888', cursor: 'help' }}>—</span>
                        </CZTooltip>
                    );
                }

                if (!cell || cell.status === 'loading') {
                    return (
                        <CZTooltip text={loadingText}>
                            <span style={{ color: '#888', cursor: 'help' }}>…</span>
                        </CZTooltip>
                    );
                }

                if (cell.status === 'no_base') {
                    return (
                        <CZTooltip text={noBaseText}>
                            <span style={{ color: '#888', cursor: 'help' }}>—</span>
                        </CZTooltip>
                    );
                }

                if (cell.status === 'skip') {
                    return (
                        <CZTooltip text={skipText}>
                            <span style={{ color: '#888', cursor: 'help' }}>—</span>
                        </CZTooltip>
                    );
                }

                if (cell.status === 'fail') {
                    return (
                        <CZTooltip text={failText}>
                            <span style={{ color: '#c00', cursor: 'help' }}>—</span>
                        </CZTooltip>
                    );
                }

                if (cell.converted === null) {
                    return (
                        <CZTooltip text={skipText}>
                            <span style={{ color: '#888', cursor: 'help' }}>—</span>
                        </CZTooltip>
                    );
                }

                const { converted, rate, dateLabel, lineCurrency, baseCurrency, profitDisplay } = cell;
                const tooltipText = tpl
                    .replace(/\{date\}/g, dateLabel)
                    .replace(/\{from\}/g, lineCurrency)
                    .replace(/\{to\}/g, baseCurrency)
                    .replace(/\{rate\}/g, formatRateForTooltip(rate))
                    .replace(/\{profit\}/g, String(profitDisplay))
                    .replace(/\{converted\}/g, currencyFormatter(baseCurrency)(converted));
                const tooltipLines = tooltipText
                    .split(/\n/g)
                    .map((line) => line.trim())
                    .filter(Boolean)
                    .map((line, idx) => <div key={`${id ?? 'fx'}_${idx}`}>{line}</div>);

                let color = 'black';
                if (converted < 0) color = 'red';
                else if (converted > 0) color = 'green';

                return (
                    <CZTooltip text={tooltipLines.length ? tooltipLines : tooltipText}>
                        <span style={{ color, cursor: 'help' }}>{currencyFormatter(baseCurrency)(converted)}</span>
                    </CZTooltip>
                );
            }
        },
        {
            field: 'sellingRate',
            sortable: false,
            isDisplayed: true
        },
        {
            field: 'buyingRate',
            sortable: false,
            isDisplayed: true
        },
        {
            field: 'chargeBy',
            sortable: false,
            title: 'Charge By',
            isDisplayed: true,
            renderer: (cellValue: string, bindData) => {
                let textValue = 'Fixed';
                if (cellValue === 'volume') {
                    textValue = 'CBM/volume';
                } else if (cellValue === 'weight') {
                    textValue = 'Weight';
                }

                return <div>{textValue}</div>;
            }
        }
        ];
    }, [localization, lineProfitInBaseCells, cargoOwnerBaseCurrency, surchargePct]);

    const [modalVisible, setVisible] = useState(false);
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [billingLine, setBillingLine] = useState({} as billingLine);
    return (
        <BillingLinesWrapper>
            {billingLines ? (
                <>
                    {!featureFlags?.UNDER_FORWARDER && !printMode ? (
                        <CustomModal
                            title={{ text: localization.billing_lines.modal.add_title }}
                            isOpen={addModalVisible}
                            Trigger={(props: ModalTriggerProps) => (
                                <Container>
                                    {billingLines.length > 0 ? (
                                        <CenteredDropdownWrapper>
                                            <DropDown
                                                options={
                                                    settings?.accountingState
                                                        ?.sort((a, b) => a.displayOrder - b.displayOrder)
                                                        .map((item) => ({
                                                            key: item.code,
                                                            text: item.name,
                                                            value: item.code,
                                                            color: item.color
                                                        })) || []
                                                }
                                                width="90%"
                                                placeHolder="Accounting State"
                                                optionRenderer={renderStateOption}
                                                onChange={handleAccountingStateChange}
                                                value={selectedState}
                                            />
                                        </CenteredDropdownWrapper>
                                    ) : (
                                        <></>
                                    )}
                                    <ToolbarButtonGroup>
                                        {toolbarActions}
                                        <Button onClick={() => setAddModalVisible(true)} width="auto" buttonType="hollow" padding="4px 12px">
                                            {localization.billing_lines.add_billingline}
                                        </Button>
                                    </ToolbarButtonGroup>
                                </Container>
                            )}
                            Content={(
                                setVisible?: React.Dispatch<React.SetStateAction<boolean>>,
                                setConfirm?: React.Dispatch<React.SetStateAction<boolean>>
                            ) => {
                                return (
                                    <AccountingFormConnected
                                        entity={entity}
                                        mode={formMode.NEW}
                                        openedFromModal={setVisible ? {
                                            setVisible: (val: boolean) => {
                                                setVisible(val);
                                                if (!val) {
                                                    setAddModalVisible(false);
                                                }
                                            }
                                        } : undefined}
                                        confirmation={setConfirm ? { setConfirm } : undefined}
                                        settings={settings}
                                        localization={localization}
                                        cargoOwnerVat={cargoOwnerVat}
                                    />
                                );
                            }}
                            onCancelCallback={() => setAddModalVisible(false)}
                            width="600px"
                        />
                    ) : (
                        <h2>Billing Lines</h2>
                    )}

                    {loading ? (
                        <Loader />
                    ) : billingLines.length > 0 ? (
                        <GridWrapper>
                            <Grid<billingLine>
                                columns={accountingGridColumns}
                                data={billingLinesForGrid}
                                localization={localization.events.grid}
                                onSettingsClick={() => console.log('settings')}
                                onRowMoreOptionsClick={(row) => {
                                    setBillingLine(row);
                                    setVisible(true);
                                }}
                                menuType={menuType}
                                settings={settings}
                            />

                            {modalVisible && (
                                <CustomModal
                                    title={{ text: localization.billing_lines.modal.edit_title }}
                                    isOpen={true}
                                    Content={(
                                        setModalVisible?: React.Dispatch<React.SetStateAction<boolean>>,
                                        setConfirm?: React.Dispatch<React.SetStateAction<boolean>>
                                    ) => {
                                        return (
                                            <AccountingFormConnected
                                                entity={entity}
                                                mode={formMode.EDIT}
                                                billingLine={billingLine}
                                                openedFromModal={{ setVisible }}
                                                confirmation={setConfirm ? { setConfirm } : undefined}
                                                settings={settings}
                                                localization={localization}
                                                cargoOwnerVat={cargoOwnerVat}
                                            />
                                        );
                                    }}
                                    onCancelCallback={() => {
                                        setVisible(false);
                                        setBillingLine({} as billingLine);
                                    }}
                                    width="393px"
                                />
                            )}
                            {billingLines && billingLines.length > 0 && (
                                <TotalCostWrapper>
                                    {Object.entries(calculateTotalProfitByCurrency()).map(([currency, total]) => (
                                        <span style={{ color: total > 0 ? 'green' : total < 0 ? 'red' : 'black', marginRight: '10px' }}>
                                            <strong>
                                                Total {currency}: {total}
                                            </strong>
                                        </span>
                                    ))}
                                    {cargoOwnerBaseCurrency && baseTotal.status === 'loading' && (
                                        <BaseProfitInOwnerCurrencyRow>
                                            <strong>
                                                {formatTotalInBaseLabel(cargoOwnerBaseCurrency)}: <SmallLoader />
                                            </strong>
                                            <CZTooltip text={baseTotalTooltip} placement="topLeft">
                                                <InfoIconButton type="button" aria-label={baseTotalInfoAria}>
                                                    <img src={infoIcon} alt="" width={16} height={16} />
                                                </InfoIconButton>
                                            </CZTooltip>
                                        </BaseProfitInOwnerCurrencyRow>
                                    )}
                                    {cargoOwnerBaseCurrency && baseTotal.status === 'ready' && (
                                        <BaseProfitInOwnerCurrencyRow
                                            style={{
                                                color: baseTotal.total > 0 ? 'green' : baseTotal.total < 0 ? 'red' : 'black'
                                            }}
                                        >
                                            <strong>
                                                {formatTotalInBaseLabel(baseTotal.currency)}:{' '}
                                                {currencyFormatter(baseTotal.currency)(baseTotal.total)}
                                            </strong>
                                            <CZTooltip text={baseTotalTooltip} placement="topLeft">
                                                <InfoIconButton type="button" aria-label={baseTotalInfoAria}>
                                                    <img src={infoIcon} alt="" width={16} height={16} />
                                                </InfoIconButton>
                                            </CZTooltip>
                                        </BaseProfitInOwnerCurrencyRow>
                                    )}
                                    {cargoOwnerBaseCurrency && baseTotal.status === 'error' && (
                                        <BaseProfitInOwnerCurrencyRow style={{ color: 'red' }}>
                                            <strong>
                                                {formatTotalInBaseLabel(cargoOwnerBaseCurrency)}: {baseTotal.message}
                                            </strong>
                                            <CZTooltip text={baseTotalTooltip} placement="topLeft">
                                                <InfoIconButton type="button" aria-label={baseTotalInfoAria}>
                                                    <img src={infoIcon} alt="" width={16} height={16} />
                                                </InfoIconButton>
                                            </CZTooltip>
                                        </BaseProfitInOwnerCurrencyRow>
                                    )}
                                </TotalCostWrapper>
                            )}
                        </GridWrapper>
                    ) : (
                        <EmaptyStateText>{localization.billing_lines.empty_state}</EmaptyStateText>
                    )}
                </>
            ) : (
                <Loader />
            )}
        </BillingLinesWrapper>
    );
}

const GridWrapper = styled.div`
    flex: 0 0 100%;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
    overflow-y: overlay;
    height: 100%;
    width: 100%;
`;

const BillingLinesWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const EmaptyStateText = styled.span`
    color: ${(props) => props.theme.colors.emptyStateText};
`;
const Container = styled.div`
    position: relative; /* Required for absolute positioning */
    display: flex;
    align-items: center;
    justify-content: flex-end; /* Keep the button on the right */
    padding-top: 8px;
    width: 100%;
`;

const ToolbarButtonGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-left: auto;
`;

const CenteredDropdownWrapper = styled.div`
    position: absolute;
    left: 55%; /* Move to the center */
    transform: translateX(-50%); /* Center the dropdown horizontally */
    width: 50%;
    display: flex;
    justify-content: center;
`;

const SmallLoader = styled(Loader)`
    width: 24px; /* Set a smaller width */
    height: 24px; /* Set a smaller height */
    margin: 0; /* Remove any margin */
`;

const TotalCostWrapper = styled.div`
    padding: 10px;
    text-align: left;
`;

const BaseProfitInOwnerCurrencyRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-right: 10px;
    margin-top: 8px;
`;

const InfoIconButton = styled.button`
    display: inline-flex;
    padding: 0;
    border: none;
    background: transparent;
    cursor: help;
    align-items: center;
    line-height: 0;
    flex-shrink: 0;
`;

export default BillingLines;
